import { isFulfilled } from "@reduxjs/toolkit";
import { FormikErrors, FormikTouched } from "formik";
import { Dispatch, SetStateAction, useMemo } from "react";
import Roles from "../../../models/roles";
import { TAdminUser, TCreateAdminFormValues } from "../../../models/superadmin";
import {
  useAppDispatch as useDispatch,
  useAppSelector as useSelector,
} from "../../../store";
import { otherRequestsThunk } from "../../../store/superadmin/thunks";
import CustomButton from "../../UI/Buttons/CustomButton";
import InactiveOutline from "../../UI/Icons/InactiveOutline";
import RoundedTick from "../../UI/Icons/RoundedTick";
import RadioInput from "../../UI/Inputs/RadioInput";
import TextInput from "../../UI/Inputs/TextInput";
import ErrorMessage from "../../UI/Tooltips/ErrorMessage";

// Define the props for the UserForm component
export interface IUserFormProps {
  // centresList: any[];
  touched: FormikTouched<TCreateAdminFormValues>; // Touched fields object from Formik
  values: TCreateAdminFormValues; // Current values of the form
  errors: FormikErrors<TCreateAdminFormValues>; // Validation errors for the form fields
  handleChooseAdminType: (value: string) => void; // Function to handle the selection of admin type (Super Admin/General Admin)
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void; // Function to handle form field changes
  handleSubmit: () => void; // Function to handle form submission
  handleTagsChange: (tags: number | number[]) => void; // expect string or array of strings
  handleAccessToAllCentresChange: (checked: boolean) => void; // Function to handle changes to the access to all centres
  handleTimesheetChange: (checked: boolean) => void; // Function to handle changes to the timesheet access
  selectedAdminDetail: TAdminUser | null;
  getAllUsers: () => Promise<void>;
  setShowModal: Dispatch<SetStateAction<boolean>>;
}

export default function UserForm(props: IUserFormProps) {
  const {
    // centresList,
    touched,
    values,
    errors,
    handleChooseAdminType,
    handleTagsChange,
    handleAccessToAllCentresChange,
    handleTimesheetChange,
    handleChange,
    handleSubmit,
    getAllUsers,
    setShowModal,
    selectedAdminDetail,
  } = props;
  const dispatch = useDispatch();
  const InProgress = useSelector((state) => state.superadmin.inProgress); // Get the inProgress state from the Redux store to manage the loading state of the button
  const makeInactiveInProgress = useSelector((state) => state.superadmin.makeInactiveInProgress); // Get the inProgress state from the Redux store to manage the loading state of the button
  const user = useSelector((state) => state.auth.user);

  const isSuperAdmin = useMemo(
    () => user?.role.name === Roles.SUPER_ADMIN,
    [user]
  );

  const handleMakeInactive = async (id: string, action: string) => {
    const payload = {
      user_uuid: id,
      action: action,
    };
    const result = await dispatch(otherRequestsThunk(payload));
    if (isFulfilled(result)) {
      getAllUsers();
      setShowModal(false);
    }
  };

  return (
    <div className="d-flex flex-column gap-15">
      <div className="fs-22 fw-400 text-black">
        {values.candidate_id !== 0 ? "Edit" : "Create"} an Admin
      </div>
      <div className="bg-off-white h-1px w-100"></div>
      <div className="fs-16 text-black">Select a user type:</div>
      <div className="d-flex flex-column gap-10">
        <div className="theme-check">
          <RadioInput
            checked={String(values.role_id) === "1"}
            label={"Super Admin"}
            onChange={handleChooseAdminType}
            value={"1"}
            name="role_id"
            themeCheck={true}
          />
        </div>
        <div className="theme-check">
          <RadioInput
            checked={String(values.role_id) === "2"}
            label={"General Admin"}
            onChange={handleChooseAdminType}
            value={"2"}
            name="role_id"
            themeCheck={true}
          />
        </div>
        {errors && <ErrorMessage message={errors.role_id as string} />}
      </div>
      <div className="w-100 h-1px"></div>
      <div className="fs-16 text-black">Add user details:</div>
      <div className="d-flex flex-column flex-lg-row gap-10">
        <div className="flex-fill">
          <TextInput
            value={values.first_name || ""}
            field={"first_name"}
            label={"First Name"}
            onChange={handleChange}
            touched={touched.first_name! as boolean}
            error={errors.first_name as string}
            placeholder={" " + ""}
          />
        </div>
        <div className="flex-fill">
          <TextInput
            value={values.last_name || ""}
            field={"last_name"}
            label={"Last Name"}
            onChange={handleChange}
            touched={touched.last_name! as boolean}
            error={errors.last_name as string}
            placeholder={" " + ""}
          />
        </div>
      </div>
      <div className="d-flex flex-column flex-lg-row gap-10">
        <div className="flex-fill">
          <TextInput
            value={values.email || ""}
            field={"email"}
            label={"Email Address"}
            onChange={handleChange}
            touched={touched.email! as boolean}
            error={errors.email as string}
            placeholder={" " + ""}
          />
        </div>
        <div className="flex-fill">
          <TextInput
            value={values.phone_number || ""}
            field={"phone_number"}
            label={"Phone Number"}
            onChange={handleChange}
            touched={touched.phone_number! as boolean}
            error={errors.phone_number as string}
            placeholder={" " + ""}
          />
        </div>
      </div>
      {/* {String(values.role_id) === "2" && (
        <>
          <div className="w-100 h-1px"></div>
          <div className="fs-16 fw-400 text-black">Assign user to centres:</div>
          <InputTags
            field={"centre_ids"}
            selectedValue={values.centre_ids}
            onTagsChange={handleTagsChange}
            allCenters={centresList}
            allcentreAccess={values?.has_all_centre_access}
          />
          {errors && <ErrorMessage message={errors.centre_ids as string} />}
          <div className="w-100 h-1px"></div>
          <CheckboxInput
            checked={values?.has_all_centre_access === 1}
            label="Access to all centres"
            id={"has_all_centre_access"}
            onChange={handleAccessToAllCentresChange}
          />
          <div className="w-100 h-1px"></div>
          <div className="fs-16 fw-400 text-black">Set permission levels:</div>
          <div className="theme-check">
            <CheckboxInput
              checked={values?.timesheet_access === 1}
              label="Allow access to Timesheet"
              id={"allowTimesheet"}
              themeCheck={true}
              onChange={handleTimesheetChange}
            />
          </div>
        </>
      )} */}
      <div className="w-100 h-1px"></div>
      <div className="d-flex flex-column flex-lg-row gap-10 justify-content-between">
        {isSuperAdmin && values.candidate_id ? (
          <CustomButton
            variant={"primary"}
            className="btn-theme bg-off-white text-black d-flex"
            onClick={() =>
              handleMakeInactive(
                selectedAdminDetail ? selectedAdminDetail?.uuid : "",
                "make_inactive"
              )
            }
            contentClass="d-flex"
            isLoading={makeInactiveInProgress}
            type="submit"
          >
            <InactiveOutline />
            Make Inactive
          </CustomButton>
        ) : (
          <div></div>
        )}
        <CustomButton
          variant={"primary"}
          type="button"
          isLoading={InProgress}
          contentClass={"d-flex"}
          className="btn-theme bg-teal text-black"
          onClick={handleSubmit}
        >
          <RoundedTick color={InProgress ? "#aaaaaa" : "#000000"} />
          {values.candidate_id !== 0 ? "Save Changes" : "Save and Invite"}
        </CustomButton>
      </div>
    </div>
  );
}
